import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import {
  getNoteApi,
  createNoteApi,
  updateNoteApi,
  markViewedApi,
  deleteNoteApi
} from '../../utils/notesData';
import { useDispatch } from 'react-redux';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { formatDuration } from '../../utils/googleMeetData';

const NoteDetail = () => {
  const { noteId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [note, setNote] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [bookmarked, setBookmarked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isNewNote, setIsNewNote] = useState(false);
  const hasContentChangedRef = useRef(false);

  // Meeting note specific states
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  
  // Check if this is a meeting note
  const isMeetingNote = useMemo(() => {
    return note?.meetingId || note?.meetingMetadata;
  }, [note]);

  useEffect(() => {
    if (noteId === 'new') {
      setIsNewNote(true);
      setNote({ _id: 'new', title: '', content: '', categories: [], bookmarked: false, updatedAt: new Date() });
      setTitle('');
      setContent('');
      setCategories([]);
      setBookmarked(false);
    } else {
      load();
    }
  }, [noteId]);

  const load = async () => {
    try {
      const n = await getNoteApi(noteId);
      if (!n) {
        dispatch(setErrorAlert('Note not found'));
        navigate('/notes');
        return;
      }
      setNote(n);
      setTitle(n.title || '');
      setContent(n.content || '');
      setCategories(n.categories || []);
      setBookmarked(n.bookmarked || false);
      markViewedApi(noteId);
    } catch (e) {
      console.error(e);
      dispatch(setErrorAlert('Failed to load note'));
      navigate('/notes');
    }
  };

  const hasChanges = useMemo(() => {
    if (!note) return false;
    const categoriesChanged = JSON.stringify(categories.sort()) !== JSON.stringify((note.categories || []).sort());
    return (title !== (note.title || '')) || (content !== (note.content || '')) || categoriesChanged;
  }, [note, title, content, categories]);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (isNewNote) {
        // Create new note
        const newNote = await createNoteApi({ title, content, categories });
        dispatch(setSuccessAlert('Note created'));
        navigate('/notes');
      } else {
        // Update existing note
        const updated = await updateNoteApi(noteId, { title, content, categories });
        setNote(updated);
        dispatch(setSuccessAlert('Note saved'));
        navigate('/notes');
      }
    } catch (e) {
      console.error(e);
      dispatch(setErrorAlert(isNewNote ? 'Failed to create note' : 'Failed to save note'));
    } finally {
      setSaving(false);
    }
  };

  const handleBookmarkToggle = async () => {
    if (isNewNote) {
      dispatch(setErrorAlert('Please save the note first'));
      return;
    }
    
    try {
      const updated = await updateNoteApi(noteId, { bookmarked: !bookmarked });
      setBookmarked(updated.bookmarked);
      setNote(updated);
      dispatch(setSuccessAlert(updated.bookmarked ? 'Note bookmarked' : 'Bookmark removed'));
    } catch (e) {
      console.error(e);
      dispatch(setErrorAlert('Failed to update bookmark'));
    }
  };

  const handleContentChange = async (newContent) => {
    setContent(newContent);
    
    // If it's a new note and user starts typing, auto-save to create the note
    if (isNewNote && !hasContentChangedRef.current && newContent.trim()) {
      hasContentChangedRef.current = true;
      try {
        const newNote = await createNoteApi({ title, content: newContent, categories });
        setNote(newNote);
        setIsNewNote(false);
        // Update the URL to the new note ID
        navigate(`/notes/${newNote._id}`, { replace: true });
        dispatch(setSuccessAlert('Note auto-saved'));
      } catch (e) {
        console.error(e);
        dispatch(setErrorAlert('Failed to auto-save note'));
      }
    }
  };

  const handleTitleChange = async (newTitle) => {
    setTitle(newTitle);
    
    // If it's a new note and user starts typing, auto-save to create the note
    if (isNewNote && !hasContentChangedRef.current && newTitle.trim()) {
      hasContentChangedRef.current = true;
      try {
        const newNote = await createNoteApi({ title: newTitle, content, categories });
        setNote(newNote);
        setIsNewNote(false);
        // Update the URL to the new note ID
        navigate(`/notes/${newNote._id}`, { replace: true });
        dispatch(setSuccessAlert('Note auto-saved'));
      } catch (e) {
        console.error(e);
        dispatch(setErrorAlert('Failed to auto-save note'));
      }
    }
  };

  const handleAddCategory = () => {
    const trimmed = newCategory.trim();
    if (trimmed && !categories.includes(trimmed)) {
      setCategories([...categories, trimmed]);
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (catToRemove) => {
    setCategories(categories.filter(c => c !== catToRemove));
  };

  const handleBack = () => {
    navigate('/notes');
  };

  if (!note) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <div className="w-full px-4">
              <div className="text-white text-[16px] font-bold mb-2 text-left">
                Note not found
              </div>
              <button
                onClick={() => navigate('/notes')}
                className="bg-[#F5F5F5] text-black px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(245,245,245,0.8)] transition-colors"
              >
                Back to Notes
              </button>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <div className="flex items-center gap-2">
              {isMeetingNote && (
                <span className="text-[#17A2B8] text-[16px]" title="Meeting Note">🎥</span>
              )}
              <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>
                {isMeetingNote ? 'Meeting Note' : 'Notes'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleBookmarkToggle}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                {bookmarked ? '★ Bookmarked' : '☆ Bookmark'}
              </button>
              <button
                disabled={!hasChanges || saving}
                onClick={handleSave}
                className={`text-[12px] font-normal cursor-pointer transition-colors ${
                  hasChanges && !saving
                    ? 'text-white hover:opacity-70'
                    : 'text-[rgba(255,255,255,0.3)] cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <span
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
                onClick={handleBack}
              >
                Back
              </span>
            </div>
          </div>

          {/* Meeting Metadata Banner (if meeting note) */}
          {isMeetingNote && note?.meetingMetadata && (
            <div className="px-4 mb-4">
              <div className="bg-[rgba(23,162,184,0.1)] border border-[rgba(23,162,184,0.3)] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#17A2B8] text-[13px] font-normal">📹 Meeting Recording</span>
                </div>
                <div className="space-y-1 text-[10px] text-[rgba(255,255,255,0.7)]">
                  {note.meetingMetadata.meetingDate && (
                    <div>
                      📅 {new Date(note.meetingMetadata.meetingDate).toLocaleDateString()} at{' '}
                      {new Date(note.meetingMetadata.meetingDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  {note.meetingMetadata.duration > 0 && (
                    <div>⏱️ Duration: {formatDuration(note.meetingMetadata.duration)}</div>
                  )}
                  {note.meetingMetadata.participants && note.meetingMetadata.participants.length > 0 && (
                    <div>
                      👥 {note.meetingMetadata.participants.length} participant{note.meetingMetadata.participants.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="w-full px-4 flex-1 overflow-y-auto">
            {/* Title Field - Matches manual add style */}
            <div className="mb-4">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#F5F5F5] hover:bg-[rgba(245,245,245,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                  Title
                </label>
                <input
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Enter note title"
                  className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                />
              </div>
            </div>

            {/* Tags/Categories Section - Matches manual add style */}
            <div className="mb-4">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#F5F5F5] hover:bg-[rgba(245,245,245,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {categories.map((cat) => (
                    <div
                      key={cat}
                      className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[rgba(245,245,245,0.2)] text-white border border-[rgba(245,245,245,0.4)]"
                    >
                      <span>{cat}</span>
                      <button
                        onClick={() => handleRemoveCategory(cat)}
                        className="text-[rgba(255,255,255,0.6)] hover:text-white"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 items-center">
                  <input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCategory();
                      }
                    }}
                    placeholder="Add a tag..."
                    className="flex-1 bg-transparent border border-[rgba(255,255,255,0.1)] rounded px-2 py-1 text-white text-[11px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#F5F5F5]"
                  />
                  <button
                    onClick={handleAddCategory}
                    className="text-[11px] px-3 py-1 rounded bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.7)] hover:bg-[rgba(255,255,255,0.15)] hover:text-white transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {/* Content Field - Matches manual add style */}
            <div className="mb-4">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#F5F5F5] hover:bg-[rgba(245,245,245,0.05)] transition-all">
                <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
                  {isMeetingNote ? 'Meeting Notes' : 'Note Content'}
                </label>
                {isMeetingNote ? (
                  /* Special rendering for meeting notes with collapsible transcript */
                  <div className="space-y-4">
                    <div 
                      className="text-[rgba(255,255,255,0.9)] text-[12px] leading-relaxed whitespace-pre-wrap prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ 
                        __html: content
                          .replace(/## 📋 Full Transcript[\s\S]*$/, '') // Remove transcript section for separate display
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/^## (.*?)$/gm, '<h2 class="text-white text-[14px] font-semibold mt-4 mb-2">$1</h2>')
                          .replace(/^- \[ \] (.*?)$/gm, '<div class="flex items-start gap-2 my-1"><input type="checkbox" class="mt-1" /><span>$1</span></div>')
                          .replace(/^- (.*?)$/gm, '<div class="flex items-start gap-2 my-1"><span class="text-[#F5F5F5]">•</span><span>$1</span></div>')
                      }}
                    />

                    {/* Collapsible Full Transcript */}
                    {content.includes('## 📋 Full Transcript') && (
                      <div className="border-t border-[rgba(255,255,255,0.1)] pt-4 mt-6">
                        <button
                          onClick={() => setIsTranscriptExpanded(!isTranscriptExpanded)}
                          className="flex items-center justify-between w-full text-left text-white text-[12px] font-normal mb-2 hover:text-[#F5F5F5] transition-colors"
                        >
                          <span>📋 Full Transcript</span>
                          <span className="transform transition-transform" style={{ transform: isTranscriptExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            ▼
                          </span>
                        </button>
                        {isTranscriptExpanded && (
                          <div 
                            className="mt-3 p-4 bg-[rgba(0,0,0,0.3)] rounded-lg text-[rgba(255,255,255,0.8)] text-[11px] leading-relaxed max-h-[400px] overflow-y-auto"
                            dangerouslySetInnerHTML={{ 
                              __html: content
                                .split('## 📋 Full Transcript')[1]
                                ?.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                                .replace(/\[(.*?)\]/g, '<span class="text-[#17A2B8]">[$1]</span>')
                            }}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Regular note textarea */
                  <textarea
                    value={content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder="Start typing your note..."
                    rows={12}
                    className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none resize-none"
                  />
                )}
              </div>
            </div>

            {/* Save Button - Matches manual add style */}
            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#F5F5F5] hover:bg-[rgba(245,245,245,0.05)] transition-all">
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="w-full bg-[#F5F5F5] text-black text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(245,245,245,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Note'
                )}
              </button>
            </div>

            <div className="flex items-center justify-start mt-4">
              <span className="text-[10px] text-[rgba(255,255,255,0.5)]">
                Last edited {new Date(note.updatedAt).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default NoteDetail;


